'use strict';

const SESSION_KEY = 'bf_css_packs';
const PACK_ORDER = ['bs', 'neu', 'bridge', 'shim'];
const SHEET_ORDER = ['bs', 'neu', 'bridge', 'shim'];
const PACK_STYLE_IDS = {
  bs: 'bf-css-bs',
  neu: 'bf-css-neu',
  bridge: 'bf-css-bridge',
  shim: 'bf-css-shim'
};
const PACK_ANCHOR_ID = 'bf-css-pack-end';

// Default matches the pre-pack cascade: Bootstrap + NeuBoard, unpatched.
const DEFAULT_CSS_PACKS = {
  bs: true,
  neu: true,
  bridge: false,
  shim: false
};

let registeredSheets = null;

function coercePackFlag(value, fallback) {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  if (value === 0 || value === '0' || value === false) {
    return false;
  }
  if (value === 1 || value === '1' || value === true) {
    return true;
  }
  return Boolean(value);
}

function readCssPacksFromSite(content) {
  const raw = content && content.layout && content.layout.cssPacks;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  return raw;
}

function resolveCssPacks({ sitePacks, sessionPacks, patch } = {}) {
  const resolved = {
    bs: DEFAULT_CSS_PACKS.bs,
    neu: DEFAULT_CSS_PACKS.neu,
    bridge: DEFAULT_CSS_PACKS.bridge,
    shim: DEFAULT_CSS_PACKS.shim
  };

  PACK_ORDER.forEach((name) => {
    resolved[name] = coercePackFlag(sitePacks && sitePacks[name], resolved[name]);
  });

  PACK_ORDER.forEach((name) => {
    if (sessionPacks && Object.prototype.hasOwnProperty.call(sessionPacks, name)) {
      resolved[name] = coercePackFlag(sessionPacks[name], resolved[name]);
    }
  });

  if (patch && typeof patch === 'object') {
    PACK_ORDER.forEach((name) => {
      if (Object.prototype.hasOwnProperty.call(patch, name)) {
        resolved[name] = coercePackFlag(patch[name], resolved[name]);
      }
    });
  }

  return resolved;
}

function applyCssPacks(packs, sheets) {
  if (!sheets) {
    return packs;
  }

  const enabled = {
    bs: Boolean(packs && packs.bs),
    neu: Boolean(packs && packs.neu),
    bridge: Boolean(packs && packs.bridge),
    shim: Boolean(packs && packs.shim)
  };

  if (enabled.bs && sheets.bs && typeof sheets.bs.setCss === 'function' && sheets.bs.raw != null) {
    sheets.bs.setCss(enabled.bridge ? patchBootstrapCss(sheets.bs.raw) : sheets.bs.raw);
  }
  if (enabled.neu && sheets.neu && typeof sheets.neu.setCss === 'function' && sheets.neu.raw != null) {
    sheets.neu.setCss(enabled.bridge ? patchNeuCss(sheets.neu.raw) : sheets.neu.raw);
  }

  const shouldUse = {
    bs: enabled.bs,
    neu: enabled.neu,
    bridge: enabled.bridge,
    shim: enabled.shim
  };

  SHEET_ORDER.forEach((name) => {
    const sheet = sheets[name];
    if (!sheet) {
      return;
    }
    if (shouldUse[name]) {
      if (typeof sheet.use === 'function') {
        sheet.use();
      }
    } else if (typeof sheet.unuse === 'function') {
      sheet.unuse();
    }
  });

  return enabled;
}

function readCssPacksFromSession() {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    const sessionPacks = {};
    PACK_ORDER.forEach((name) => {
      if (Object.prototype.hasOwnProperty.call(parsed, name)) {
        sessionPacks[name] = coercePackFlag(parsed[name], DEFAULT_CSS_PACKS[name]);
      }
    });
    return sessionPacks;
  } catch (e) {
    return null;
  }
}

function writeCssPacksToSession(packs) {
  try {
    const payload = resolveCssPacks({ sessionPacks: packs, patch: packs });
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
    return payload;
  } catch (e) {
    return null;
  }
}

function clearCssPacksFromSession() {
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch (e) {
    // ignore quota / private-mode errors
  }
}

function cssToText(css) {
  if (typeof css === 'string') {
    return css;
  }
  if (css == null) {
    return '';
  }
  if (Array.isArray(css)) {
    return css.map((item) => Array.isArray(item) ? item[1] : cssToText(item)).join('\n');
  }
  return String(css);
}

function patchBootstrapCss(css) {
  return cssToText(css)
    .replace('.hidden{display:none!important}', '.hidden{display:none}')
    .replace(
      '}a{color:#337ab7;text-decoration:none}a:focus,a:hover{color:#23527c;text-decoration:underline}',
      '}a{color:inherit;text-decoration:none}a:focus,a:hover{text-decoration:underline}'
    );
}

function patchNeuCss(css) {
  return cssToText(css)
    .replace(
      /a:link,\s*a:visited \{([^}]*?) {2}color: #2e2e32;\n/m,
      'a:link,\na:visited {$1'
    )
    .replace(
      /a:hover,\s*a:active \{([^}]*?) {2}color: #999999;\n/m,
      'a:hover,\na:active {$1'
    );
}

function ensurePackAnchor() {
  let anchor = document.getElementById(PACK_ANCHOR_ID);
  if (anchor) {
    return anchor;
  }
  anchor = document.createElement('meta');
  anchor.id = PACK_ANCHOR_ID;
  document.head.appendChild(anchor);
  return anchor;
}

function insertPackStyle(el, id) {
  const laterIds = Object.values(PACK_STYLE_IDS);
  const idx = laterIds.indexOf(id);
  for (let i = idx + 1; i < laterIds.length; i += 1) {
    const later = document.getElementById(laterIds[i]);
    if (later && later.parentNode) {
      later.parentNode.insertBefore(el, later);
      return;
    }
  }
  document.head.insertBefore(el, ensurePackAnchor());
}

function createStyleSheetController(cssText, id) {
  const raw = cssToText(cssText);
  let current = raw;
  let el = null;

  return {
    raw,
    setCss(next) {
      current = cssToText(next);
      if (el) {
        el.textContent = current;
      }
    },
    use() {
      if (el && el.parentNode) {
        return;
      }
      el = document.createElement('style');
      el.id = id;
      el.setAttribute('data-bf-css-pack', id.replace(/^bf-css-/, ''));
      el.textContent = current;
      insertPackStyle(el, id);
    },
    unuse() {
      if (!el) {
        return;
      }
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
      el = null;
    }
  };
}

function registerCssPackSheets(sheets) {
  registeredSheets = sheets || null;
}

function getRegisteredCssPackSheets() {
  return registeredSheets;
}

function applyResolvedCssPacks(packs) {
  return applyCssPacks(packs, registeredSheets);
}

function bootCssPacks(content) {
  const packs = resolveCssPacks({
    sitePacks: readCssPacksFromSite(content),
    sessionPacks: readCssPacksFromSession() || {}
  });
  applyResolvedCssPacks(packs);
  return packs;
}

export {
  SESSION_KEY,
  PACK_ORDER,
  DEFAULT_CSS_PACKS,
  coercePackFlag,
  readCssPacksFromSite,
  resolveCssPacks,
  applyCssPacks,
  readCssPacksFromSession,
  writeCssPacksToSession,
  clearCssPacksFromSession,
  cssToText,
  patchBootstrapCss,
  patchNeuCss,
  createStyleSheetController,
  registerCssPackSheets,
  getRegisteredCssPackSheets,
  applyResolvedCssPacks,
  bootCssPacks
};
